import { LegalShell } from "@/components/legal/LegalShell";

const LAST_UPDATED = "July 19, 2026";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          Last Bookmark is a marketplace that connects fiction authors with beta readers. It is
          operated from Florida, United States. In this Privacy Policy, "we," "us," and "Last
          Bookmark" refer to the operator of the platform; "you" refers to the person using it.
        </p>
        <p>
          You can reach us any time at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "info-we-collect",
    title: "Information we collect",
    body: (
      <>
        <p>We only collect what we actually need to run the platform:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Account details</strong> — your email address, a password (stored only as a
            secure one-way hash — we never see the plain-text password), your display name, and
            the roles you sign up for (author, beta reader, or both).
          </li>
          <li>
            <strong>Profile information</strong> — anything you choose to add to your profile:
            your bio, headline, location, avatar, banner image, genres, and links you share.
            You can edit or remove any of this at any time.
          </li>
          <li>
            <strong>Content you post</strong> — projects, applications, messages, coffee-chat
            requests, reviews, certification applications, and manuscripts you upload for a
            specific reader.
          </li>
          <li>
            <strong>Beta acknowledgment</strong> — the version of the Beta Terms you accepted
            and the timestamp, so we know whether to re-prompt you.
          </li>
          <li>
            <strong>Email preferences</strong> — whether you opted in to product-update emails,
            and when you gave (or declined) consent.
          </li>
          <li>
            <strong>Technical logs</strong> — standard server logs that record the routes you
            hit, IP address, browser user-agent, and timestamps. We use these to debug issues
            and to protect the platform against abuse.
          </li>
        </ul>
        <p>
          We do <strong>not</strong> collect payment card numbers, bank details, or government
          identifiers during the beta phase because we don't currently process payments.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: "How we use your information",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li>To run the platform — authenticating you, showing you your profile and content, and letting you interact with other users.</li>
          <li>To send you transactional notifications you'd expect (a new message, an application update, a certification decision).</li>
          <li>To send occasional product-update emails <strong>only if you opted in</strong>.</li>
          <li>To keep the site safe — detecting abuse, rate-limiting registration attempts, and moderating content that violates our{" "}
            <a href="/community-guidelines" className="link-underline">Community Guidelines</a>.</li>
          <li>To improve the platform — understanding which features are used and where users get stuck.</li>
        </ul>
        <p>
          <strong>We do not sell your personal information.</strong> We do not run behavioural
          ad networks or share your data with advertisers.
        </p>
      </>
    ),
  },
  {
    id: "how-we-protect",
    title: "How we protect your information",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li>All traffic to Last Bookmark is served over HTTPS with TLS encryption in transit.</li>
          <li>Passwords are stored as bcrypt hashes only — never in plain text.</li>
          <li>Authentication uses HttpOnly cookies with modern flags (SameSite=None, Secure) to reduce cross-site risk.</li>
          <li>Manuscripts and uploaded files are stored in access-scoped object storage; only the parties you shared them with can retrieve them.</li>
          <li>Sensitive admin actions such as user-visibility overrides and moderation decisions are recorded in an internal audit log. Access to production systems is restricted to authorised operators.</li>
          <li>We monitor server logs and update dependencies as security advisories are published.</li>
        </ul>
        <p>
          No online platform is 100% secure. We do our best, and we ask you to help by choosing
          a strong password and not reusing it from other services.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Who can see your information",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li>Anything you put on your <strong>public profile</strong> is visible to other signed-in users of Last Bookmark. If you want something private, don't put it on your profile.</li>
          <li>Messages, applications, and coffee-chat conversations are visible only to the participants involved and to Last Bookmark administrators when investigating a report or safety issue.</li>
          <li>Manuscripts are visible only to the reader you shared them with (and to admins investigating a report).</li>
          <li>Administrative and moderator staff may access account data as necessary to run the platform, respond to reports, or comply with law.</li>
          <li>We may share information with service providers who help us operate the platform (e.g. hosting, transactional email once enabled) under contracts that limit their use of the data.</li>
          <li>We may disclose information when required by law or valid legal process, or to protect the rights and safety of Last Bookmark, our users, and the public.</li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep your information",
    body: (
      <>
        <p>
          We keep your account data for as long as your account is active. To close your
          account, email us at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>{" "}
          from the address on your account. On receipt we remove your profile from public view
          within a few business days and delete or anonymise your personal data within 30 days,
          except:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Messages you sent to another user remain in that user's inbox (as a copy).</li>
          <li>Reviews you left about another user remain on their profile (attribution can be anonymised on request).</li>
          <li>Records we're required to keep for legal, tax, or dispute-resolution reasons.</li>
        </ul>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Access</strong> the personal data we hold about you.</li>
          <li><strong>Correct</strong> anything that's wrong — most of this you can do yourself from Settings.</li>
          <li><strong>Delete</strong> your account and associated personal data (see the previous section for what's kept).</li>
          <li><strong>Withdraw email consent</strong> at any time from Settings → Email Preferences, or by contacting us.</li>
          <li><strong>Export</strong> your data — email us and we'll provide a machine-readable copy of the information tied to your account.</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>{" "}
          from the address on your account.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <>
        <p>
          Last Bookmark is intended for adults. You must be <strong>at least 18 years old</strong>{" "}
          to create an account and use the platform. We do not knowingly collect personal
          information from anyone under 13, and any accounts identified as belonging to minors
          will be removed.
        </p>
      </>
    ),
  },
  {
    id: "us-users",
    title: "For U.S. users",
    body: (
      <>
        <p>
          Last Bookmark is operated from Florida, U.S.A. If you are a U.S. resident, U.S.
          federal and Florida state privacy law apply to how we handle your data. California
          residents have specific rights under the California Consumer Privacy Act (CCPA/CPRA)
          — those rights are already reflected in the "Your rights" section above. You may
          submit CCPA requests to{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "international",
    title: "For users outside the United States",
    body: (
      <>
        <p>
          If you access Last Bookmark from outside the United States, you understand that your
          information is processed and stored in the U.S. and that U.S. law will apply. We
          honour the substantive access, correction, and deletion rights described above for
          all users, regardless of jurisdiction.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <>
        <p>
          As Last Bookmark grows, this policy may need to change. When we make material
          updates, we'll bump the "Last updated" date at the top of this page and, for
          significant changes, surface a notice inside the app.
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalShell
      eyebrow="Privacy"
      title="Privacy Policy"
      intro="What we collect, how we use it, how we protect it, and the choices you have. Written in plain English for the way Last Bookmark actually works today."
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
      testid="privacy-policy"
    />
  );
}
